import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, AwardIcon, ChevronRightIcon, EyeIcon, FlaskConicalIcon, HeartPulseIcon, HexagonIcon, ImageIcon, LineChartIcon, SettingsIcon } from 'lucide-react';
import { ActivityContext } from '../components/ActivityContext';
import { BiomarkerGlyph, HeartGlyph, HiveGlyph, ProgressGlyph, RunningGlyph, TrophyGlyph } from '../components/icons/BeelyvIcons';
import { Avatar } from '../components/Avatar';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card, SectionTitle } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { people } from '../data/people';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import { useProgress } from '../contexts/ProgressContext';
import { useSocial } from '../contexts/SocialContext';
export function Profile() {
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const social = useSocial();
  const progress = useProgress();
  const {
    profile
  } = useMetrics();
  const me = people[0];
  const myPosts = social.posts.filter(post => post.authorId === 'me');
  const myPulse = social.pulses.find(ring => ring.authorId === 'me');
  const links = [{
    key: 'health.title',
    to: '/perfil/saude',
    Icon: HeartGlyph
  }, {
    key: 'health.biomarkers',
    to: '/perfil/saude/biomarcadores',
    Icon: BiomarkerGlyph
  }, {
    key: 'metrics.sports.title',
    to: '/perfil/esportes',
    Icon: RunningGlyph
  }, {
    key: 'metrics.charts.title',
    to: '/perfil/grafico/body',
    Icon: ProgressGlyph
  }, {
    key: 'eng.hive.entry',
    to: '/perfil/colmeia',
    Icon: HiveGlyph
  }, {
    key: 'eng.photos.entry',
    to: '/perfil/fotos',
    Icon: ImageIcon
  }, {
    key: 'visibility.preview',
    to: '/perfil/publico',
    Icon: EyeIcon
  }];
  return <div className="px-2 pb-2" data-id="element-3530" data-mp="37u80w6kgsb1o">
      <PageHeader title={t('nav.profile')} action={<button type="button" onClick={() => navigate('/config')} aria-label={t('settings.title')} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-3532" data-mp="3exoagm8en4hb">
            <SettingsIcon size={19} data-id="element-3533" data-mp="3tmkrh8sfvru2" />
          </button>} data-id="element-3531" data-mp="37uztr9mofuo9" />

      <Card className="p-2" data-id="element-3534" data-mp="2fhz4e0v7fi9">
        <div className="flex items-start gap-2" data-id="element-3535" data-mp="2e6mrptcszfj">
          <Avatar src={me.avatar} name={profile.name} size={68} ring={myPulse ? myPulse.seen ? 'seen' : 'unseen' : 'none'} onClick={myPulse ? () => navigate(`/pulse/${myPulse.id}`) : undefined} label={`${t('pulse.open')} — ${profile.name}`} data-id="element-3536" data-mp="2hkm0p6q0sir" />
          <div className="min-w-0 flex-1" data-id="element-3537" data-mp="2d7swco3ws6h2">
            <p className="bee-overline text-[9px] font-bold text-gold-deep" data-id="element-3538" data-mp="2d8ud997lvn6p">
              {t('common.level')} {t(progress.level.key)} ·{' '}
              {t('post.streak', {
              n: progress.streak
            })}
            </p>
            <p className="mt-[2px] truncate text-[17px] font-bold text-deep" data-id="element-3539" data-mp="2aoxzap41e3vu">
              {profile.name}
            </p>
            <p className="truncate text-[12px] text-muted" data-id="element-3540" data-mp="20x1pigsl8pbs">{profile.username}</p>
          </div>
        </div>

        <p className="mt-2 text-[13px] leading-relaxed text-deep" data-id="element-3541" data-mp="1vygxv5rejdyy">{profile.bio}</p>

        <dl className="mt-2 flex items-center gap-3" data-id="element-3542" data-mp="2tlpbkel5viqe">
          {[{
          key: 'profile.posts',
          value: me.counts.posts + myPosts.length
        }, {
          key: 'profile.followers',
          value: me.counts.followers
        }, {
          key: 'profile.followingCount',
          value: me.counts.following
        }].map(item => <div key={item.key} data-id="element-3543" data-mp="2eoyeibyymj3y">
              <dd className="bee-num text-[16px] font-bold leading-none text-deep" data-id="element-3544" data-mp="2enwz5vawvagf">
                {item.value}
              </dd>
              <dt className="mt-[2px] text-[10.5px] text-muted" data-id="element-3545" data-mp="18605a1a1g3le">{t(item.key)}</dt>
            </div>)}
        </dl>

        <Button variant="outline" size="lg" fullWidth className="mt-2" onClick={() => navigate('/perfil/editar')} data-id="element-3546" data-mp="2s2xhxmf1gkzj">
          {t('profile.edit')}
        </Button>
      </Card>

      <section className="mt-3" data-id="element-3547" data-mp="2i6m0yj4i76yr">
        <SectionTitle data-id="element-3548" data-mp="2i3gd177m2b4m">{t('profile.achievements')}</SectionTitle>
        <ul className="flex flex-wrap gap-1" data-id="element-3549" data-mp="2n55rkwu1l6xg">
          {me.badges.map(badge => <li key={badge} className="flex min-h-[38px] items-center gap-1 rounded-pill border border-gold/45 bg-white/70 px-2 text-[12px] font-medium text-deep" data-id="element-3550" data-mp="3av041dag8th6">
              <TrophyGlyph size={16} className="text-gold-deep" aria-hidden="true" data-id="element-3551" data-mp="30uzevapwfj52" />
              {t(badge)}
            </li>)}
        </ul>
      </section>

      <section className="mt-3" data-id="element-3552" data-mp="1pgvpyiy64l6v">
        <SectionTitle data-id="element-3553" data-mp="1pfube4hy1gke">{t('profile.metricsSection')}</SectionTitle>
        <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3554" data-mp="1ug88iri0b63n">
          {links.map(({
          key,
          to,
          Icon
        }) => <li key={to} className="border-b border-sand-line last:border-b-0" data-id="element-3555" data-mp="ch0pzbk0zpds">
              <button type="button" onClick={() => navigate(to)} className="flex min-h-[56px] w-full items-center gap-2 py-2 text-left" data-id="element-3556" data-mp="7js8zeam5re4">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[12px] bg-sand-deep text-soft" data-id="element-3557" data-mp="hgwx3jo0rwxk">
                  <Icon size={17} data-id="element-3558" data-mp="3py1f1robq42k" />
                </span>
                <span className="flex-1 text-[13.5px] font-medium text-deep" data-id="element-3559" data-mp="3kxvj3zb0tveo">
                  {t(key)}
                </span>
                <ChevronRightIcon size={16} className="shrink-0 text-muted" aria-hidden="true" data-id="element-3560" data-mp="2k5u70f50337" />
              </button>
            </li>)}
        </ul>
      </section>

      <section className="mt-3" data-id="element-3561" data-mp="366wh574oy542">
        <SectionTitle data-id="element-3562" data-mp="327nqwc8v0pd9">{t('profile.posts')}</SectionTitle>
        {myPosts.length === 0 ? <EmptyState title={t('profile.noPosts.title')} body={t('profile.noPosts.body')} data-id="element-3563" data-mp="1571at7hiiza7" /> : <ul className="grid grid-cols-2 gap-2" data-id="element-3564" data-mp="1a4jmv6qttumf">
            {myPosts.map(post => <li key={post.id} data-id="element-3565" data-mp="1exm8mspd3zs2">
                <button type="button" onClick={() => navigate(`/post/${post.id}`)} className="block w-full text-left" data-id="element-3566" data-mp="1excohlr00mua">
                  <img src={post.media} alt="" aria-hidden="true" className="aspect-square w-full rounded-[12px] object-cover" data-id="element-3567" data-mp="m9qcpq7mabwc" />
                  <span className="mt-1 block" data-id="element-3568" data-mp="w8ftffns1qb0">
                    <ActivityContext context={post.context} data-id="element-3569" data-mp="1167qeo3a42m2" />
                  </span>
                  <span className="mt-[2px] block text-[11.5px] leading-snug text-deep" data-id="element-3570" data-mp="27m1jd31gnvxl">
                    {tl(post.caption)}
                  </span>
                </button>
              </li>)}
          </ul>}
      </section>
    </div>;
}